/**
 * SLA panels reference — every state of the timer pill side-by-side.
 *
 * Mirrors design_handoff_beacon/src/screen-sla-panels.jsx, which is
 * the canonical visual spec for the SLA timer (decisions doc #2 + #6).
 * Used by ops as a quick reference and by future contributors as the
 * spec to validate the timer against.
 */

import { Card } from '@/components/ui/card.js';
import { SLA } from '@/components/beacon/sla.js';
import type { SlaState } from '@beacon/core';

interface Row {
  state: SlaState;
  label: string;
  scenario: string;
}

const ROWS: Row[] = [
  { state: 'ok', label: '4h 10m to response', scenario: 'Plenty of time, in-hours' },
  { state: 'warn', label: '58m to response', scenario: '<25% of target window remains' },
  { state: 'breaching', label: '12m to solution', scenario: '<10% — paging warning fires' },
  { state: 'breached', label: '23m overdue', scenario: 'Past the effective deadline' },
  { state: 'paused', label: 'Paused — awaiting client', scenario: 'Status = awaiting_client' },
  { state: 'paused', label: 'Paused — outside business hours', scenario: 'Mon–Fri 09:00–17:00 default' },
  { state: 'met', label: '', scenario: 'Resolved within target' },
];

export function SLAPanelsReference() {
  return (
    <div className="p-8 max-w-[860px]">
      <h1 className="text-[20px] font-semibold mb-1">SLA timer — canonical states</h1>
      <p className="text-[13px] text-ink-300 mb-6">
        Every state of the SLA timer pill. This is the spec to validate the live
        timer against. See <span className="mono">design_handoff_beacon/src/screen-sla-panels.jsx</span>{' '}
        for the canonical reference.
      </p>
      <Card padded>
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-left text-[10.5px] uppercase tracking-[0.08em] text-ink-300 font-semibold border-b border-ink-600">
              <th className="py-2 pr-4 w-[50px]">State</th>
              <th className="py-2 pr-4 w-[260px]">Pill</th>
              <th className="py-2">Scenario</th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map((r, i) => (
              <tr key={i} className="border-b border-ink-600/60 align-middle">
                <td className="py-3 pr-4 mono text-ink-200">{r.state}</td>
                <td className="py-3 pr-4">
                  <SLA state={r.state} label={r.label || undefined} />
                </td>
                <td className="py-3 text-ink-200">{r.scenario}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
